import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { API } from "../context/AuthContext";
import { Trophy, Flag, Zap, Users, Award, TrendingUp, Play } from "lucide-react";

export default function DemoPaddock() {
  const [data, setData] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    axios.get(`${API}/demo/paddock`).then(({ data }) => setData(data));
  }, []);

  if (!data) return <div className="min-h-screen grain-bg pt-20 text-center text-slate-400">Caricamento Demo Paddock…</div>;

  return (
    <div className="min-h-screen grain-bg">
      <Navbar variant="pro" />
      <section className="pt-24 pb-8">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-2">
            <div>
              <div className="pill-badge mb-3"><Play className="w-3 h-3" /> RALLY LOVE PRO · DEMO PADDOCK</div>
              <h1 className="font-display font-black text-4xl md:text-6xl leading-none" data-testid="demo-rally-name">{data.rally_name}</h1>
              <div className="font-mono-rl text-sm text-slate-400 mt-2">{data.location} · {data.dates} · {data.stages} PROVE SPECIALI · <span className="text-[#FF9F0A]">DATI DEMO</span></div>
              {data.disclaimer && <p className="text-[11px] text-slate-500 mt-2 max-w-xl" data-testid="demo-paddock-disclaimer">{data.disclaimer}</p>}
            </div>
            <div className="flex gap-2">
              <button className="btn-outline-rl" onClick={() => nav("/register?role=ORGANIZER")} data-testid="demo-create-rally-btn">CREA IL TUO RALLY</button>
              <button className="btn-crimson" onClick={() => nav("/pricing")} data-testid="demo-activate-pro-btn">ATTIVA PRO</button>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-6">
          {/* Leaderboard */}
          <div className="lg:col-span-8 rl-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-2xl flex items-center gap-2"><Trophy className="w-6 h-6 text-[#FF9F0A]" /> Classifica dimostrativa</h2>
              <p className="text-xs text-[#FF9F0A] mt-1" data-testid="demo-lb-disclaimer">CLASSIFICA DIMOSTRATIVA · Utenti, punteggi e premi presenti in questa classifica sono completamente fittizi.</p>
              <div className="pill-badge pill-amber">PS 04 / 06 · SIMULATA</div>
            </div>
            <div className="space-y-2">
              {data.leaderboard.map(r => (
                <div key={r.pos} data-testid={`leaderboard-row-${r.pos}`}
                  className={`flex items-center justify-between border rounded-lg px-4 py-4 ${r.pos === 1 ? "border-[#FF9F0A]/40 bg-[#FF9F0A]/5" : "border-white/5"}`}>
                  <div className="flex items-center gap-4">
                    <div className={`font-display font-black text-3xl w-10 ${r.pos === 1 ? "text-[#FF9F0A]" : r.pos === 2 ? "text-slate-300" : r.pos === 3 ? "text-orange-500" : "text-slate-600"}`}>P{r.pos}</div>
                    <div>
                      <div className="font-semibold text-lg">{r.driver}</div>
                      <div className="text-xs text-slate-500">Nav: {r.codriver} · {r.team}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono-rl text-xl font-bold">{r.time}</div>
                    <div className="text-xs text-slate-500 font-mono-rl">{r.points} pts</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="lg:col-span-4 space-y-4">
            <div className="rl-card p-6">
              <div className="flex items-center gap-2 text-xs font-mono-rl tracking-widest text-slate-400 uppercase mb-3"><Users className="w-4 h-4 text-[#00F0FF]" /> Coinvolgimento</div>
              <div className="grid grid-cols-2 gap-3">
                <Stat label="Fan" value={data.stats.fans.toLocaleString()} />
                <Stat label="Quiz" value={data.stats.quiz_completions.toLocaleString()} />
                <Stat label="Scansioni QR" value={data.stats.qr_scans.toLocaleString()} />
                <Stat label="Engagement" value={`${data.stats.engagement_rate}%`} color="text-[#30D158]" />
              </div>
            </div>
            <div className="rl-card p-6">
              <div className="flex items-center gap-2 text-xs font-mono-rl tracking-widest text-slate-400 uppercase mb-3"><Zap className="w-4 h-4 text-[#FF1E27]" /> Sfide Live</div>
              <div className="space-y-3">
                {data.challenges.map((c, i) => (
                  <div key={i}>
                    <div className="text-sm font-semibold">{c.title}</div>
                    <div className="text-xs text-slate-500 font-mono-rl">{c.participants.toLocaleString()} FAN · {c.reward}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sponsors */}
          <div className="lg:col-span-12">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-2xl flex items-center gap-2"><Award className="w-6 h-6 text-[#FF1E27]" /> Attivazioni Sponsor (esempio)</h2>
              <span className="pill-badge">SPONSOR FITTIZI</span>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {data.sponsors.map((s, i) => (
                <div key={i} data-testid={`sponsor-${s.name.toLowerCase()}`} className="rl-card p-6">
                  <div className="pill-badge pill-amber mb-3">{s.type.replace("_", " ")}</div>
                  <div className="font-display font-black text-2xl mb-1">{s.name}</div>
                  <div className="text-sm text-slate-300 mb-4">{s.challenge}</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-xs font-mono-rl text-slate-500 tracking-widest">PARTECIPANTI</div>
                      <div className="font-mono-rl font-bold text-lg">{s.participants.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-xs font-mono-rl text-slate-500 tracking-widest">ENGAGEMENT</div>
                      <div className="font-mono-rl font-bold text-lg text-[#30D158]">{s.engagement}%</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center px-5">
          <h2 className="headline-huge text-3xl md:text-5xl mb-4">Questo può essere il tuo rally.</h2>
          <p className="text-slate-400 mb-6">Attiva Rally Love PRO e trasforma il tuo evento in un'esperienza Paddock completamente digitale.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <button className="btn-crimson" onClick={() => nav("/register?role=ORGANIZER")} data-testid="cta-create-your-rally">CREA IL TUO RALLY</button>
            <button className="btn-outline-rl" onClick={() => nav("/pricing")} data-testid="cta-activate-pro">ATTIVA PRO</button>
          </div>
        </div>
      </section>
    </div>
  );
}

const Stat = ({ label, value, color = "text-white" }) => (
  <div>
    <div className="text-xs font-mono-rl text-slate-500 tracking-widest">{label}</div>
    <div className={`font-display font-black text-2xl ${color}`}>{value}</div>
  </div>
);
